// src/features/dashboard/components/ManagerDashboard/ProductivityReport/AssociateProductivityTable.tsx

import {
  AssociateProductivityResponse,
} from '../../../types/Manager/productivityReport.types';

interface Props {
  data: AssociateProductivityResponse[];
}


// ============================================================================
// PRODUCTIVITY BAR COLOR
// ============================================================================

const getBarColor = (
  value: number,
) => {

  if (value >= 75) {
    return 'bg-green-500';
  }

  if (value >= 60) {
    return 'bg-amber-500';
  }

  return 'bg-red-500';
};


// ============================================================================
// STATUS COLOR
// ============================================================================

const getStatusColor = (
  status: string,
) => {

  switch (
    status.toUpperCase()
  ) {

    case 'ON TRACK':

      return 'bg-green-600 text-white';

    case 'WATCH':

      return 'bg-yellow-400 text-black';

    case 'AT RISK':

      return 'bg-red-500 text-white';

    default:

      return 'bg-slate-500 text-white';
  }
};


// ============================================================================
// COMPONENT
// ============================================================================

const AssociateProductivityTable = ({
  data,
}: Props) => {


  // ==========================================================================
  // SPAN AVERAGE
  // ==========================================================================

  const spanAverage = {

    current:
      data.reduce(
        (
          sum,
          item,
        ) =>
          sum +
          item.currentProductivity,
        0,
      ) /
      (data.length || 1),

    previous:
      data.reduce(
        (
          sum,
          item,
        ) =>
          sum +
          item.previousProductivity,
        0,
      ) /
      (data.length || 1),

    change:
      data.reduce(
        (
          sum,
          item,
        ) =>
          sum +
          item.changePercent,
        0,
      ) /
      (data.length || 1),
  };


  // ==========================================================================
  // RETURN
  // ==========================================================================

  return (

    <div
      className="
        w-full
        min-w-0

        rounded-[18px]

        border
        border-slate-200

        bg-white

        p-5

        /* ================================================================
           110% ZOOM
        ================================================================= */

        [@media(max-width:1100px)]:rounded-[14px]
        [@media(max-width:1100px)]:p-3.5

        /* ================================================================
           125% - 150% ZOOM
        ================================================================= */

        [@media(max-width:1000px)]:rounded-[12px]
        [@media(max-width:1000px)]:p-2.5
      "
    >


      {/* ==================================================================
          HEADER
      ================================================================== */}

      <div
        className="
          mb-4
          flex
          min-w-0
          items-center
          gap-3

          [@media(max-width:1100px)]:mb-3
          [@media(max-width:1100px)]:gap-2

          [@media(max-width:1000px)]:mb-2
          [@media(max-width:1000px)]:gap-1.5
        "
      >

        {/* ================================================================
            TITLE
        ================================================================= */}

        <h3
          className="
            shrink-0
            whitespace-nowrap

            text-[15px]
            font-semibold
            leading-5

            text-[#003E7E]

            [@media(max-width:1100px)]:text-[12px]
            [@media(max-width:1100px)]:leading-4

            [@media(max-width:1000px)]:text-[10px]
            [@media(max-width:1000px)]:leading-3
          "
        >
          Associate Productivity Detail
        </h3>


        {/* ================================================================
            DESCRIPTION
        ================================================================= */}

        <span
          className="
            min-w-0
            max-w-full
            truncate

            rounded-full

            bg-blue-50

            px-3
            py-1

            text-[10px]
            leading-3

            text-blue-600

            [@media(max-width:1100px)]:px-2.5
            [@media(max-width:1100px)]:py-[3px]
            [@media(max-width:1100px)]:text-[8px]

            [@media(max-width:1000px)]:px-2
            [@media(max-width:1000px)]:py-[2px]
            [@media(max-width:1000px)]:text-[7px]
          "
          title="
            Ranked by productivity % · Click a row to view app-level breakdown
          "
        >
          Ranked by productivity % · Click a row to view app-level breakdown
        </span>

      </div>


      {/* ==================================================================
          TABLE SCROLLER
      ================================================================== */}

      <div
        className="
          w-full
          min-w-0

          overflow-x-auto
          overflow-y-hidden

          overscroll-x-contain

          rounded-[8px]

          scrollbar-thin
        "
      >

        {/* =================================================================
            TABLE

            IMPORTANT:
            min-width prevents the columns from getting squeezed when
            browser zoom is 110%, 125%, 150%, etc.
        ================================================================== */}

        <table
          className="
            w-full

            min-w-[1080px]

            table-auto

            text-[12px]

            text-[#101828]

            /* ==============================================================
               110% ZOOM
            =============================================================== */

            [@media(max-width:1100px)]:min-w-[970px]
            [@media(max-width:1100px)]:text-[10px]

            /* ==============================================================
               125% - 150% ZOOM
            =============================================================== */

            [@media(max-width:1000px)]:min-w-[900px]
            [@media(max-width:1000px)]:text-[9px]
          "
        >


          {/* ================================================================
              COLUMN WIDTHS
          ================================================================= */}

          <colgroup>

            {/* # */}
            <col
              className="
                w-[35px]
              "
            />

            {/* Associate */}
            <col
              className="
                w-[145px]
              "
            />

            {/* Employee Code */}
            <col
              className="
                w-[105px]
              "
            />

            {/* Team Leader */}
            <col
              className="
                w-[125px]
              "
            />

            {/* Shift */}
            <col
              className="
                w-[90px]
              "
            />

            {/* Location */}
            <col
              className="
                w-[100px]
              "
            />

            {/* Current */}
            <col
              className="
                w-[175px]
              "
            />

            {/* Previous */}
            <col
              className="
                w-[90px]
              "
            />

            {/* Change */}
            <col
              className="
                w-[90px]
              "
            />

            {/* Status */}
            <col
              className="
                w-[85px]
              "
            />

          </colgroup>


          {/* =================================================================
              HEADER
          ================================================================= */}

          <thead>

            <tr
              className="
                border-b
                border-slate-200

                text-left

                font-semibold
                text-[#101828]
              "
            >

              {/* # */}

              <th
                className="
                  whitespace-nowrap
                  py-2
                  pr-2

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1100px)]:pr-1.5

                  [@media(max-width:1000px)]:py-1
                  [@media(max-width:1000px)]:pr-1
                "
              >
                #
              </th>


              {/* Associate */}

              <th
                className="
                  whitespace-nowrap
                  py-2
                  pr-2

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1000px)]:py-1
                "
              >
                Associate
              </th>


              {/* Employee Code */}

              <th
                className="
                  py-2
                  pr-2
                  leading-4

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1000px)]:py-1
                "
              >
                Employee
                <br />
                Code
              </th>


              {/* Team Leader */}

              <th
                className="
                  whitespace-nowrap
                  py-2
                  pr-2

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1000px)]:py-1
                "
              >
                Team Leader
              </th>


              {/* Shift */}

              <th
                className="
                  whitespace-nowrap
                  py-2
                  pr-2

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1000px)]:py-1
                "
              >
                Shift
              </th>


              {/* Location */}

              <th
                className="
                  whitespace-nowrap
                  py-2
                  pr-2

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1000px)]:py-1
                "
              >
                Location
              </th>


              {/* Current */}

              <th
                className="
                  whitespace-nowrap
                  py-2
                  pr-2

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1000px)]:py-1
                "
              >
                Current %
              </th>


              {/* Previous */}

              <th
                className="
                  py-2
                  pr-2
                  leading-4

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1000px)]:py-1
                "
              >
                Previous
                <br />
                %
              </th>


              {/* Change */}

              <th
                className="
                  whitespace-nowrap
                  py-2
                  pr-2

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1000px)]:py-1
                "
              >
                Change
              </th>


              {/* Status */}

              <th
                className="
                  whitespace-nowrap
                  py-2

                  [@media(max-width:1100px)]:py-1.5
                  [@media(max-width:1000px)]:py-1
                "
              >
                Status
              </th>

            </tr>

          </thead>


          {/* =================================================================
              BODY
          ================================================================= */}

          <tbody>

            {data.map(
              (
                row,
                index,
              ) => (

                <tr
                  key={
                    row.employeeCode
                  }
                  className="
                    border-b
                    border-slate-100

                    hover:bg-slate-50
                  "
                >

                  {/* ========================================================
                      NUMBER
                  ========================================================= */}

                  <td
                    className="
                      whitespace-nowrap
                      py-2
                      pr-2

                      [@media(max-width:1100px)]:py-1.5

                      [@media(max-width:1000px)]:py-1
                    "
                  >
                    {index + 1}
                  </td>


                  {/* ========================================================
                      ASSOCIATE
                  ========================================================= */}

                  <td
                    className="
                      max-w-[145px]
                      py-2
                      pr-2

                      font-medium

                      leading-4

                      [@media(max-width:1100px)]:py-1.5
                      [@media(max-width:1100px)]:leading-3

                      [@media(max-width:1000px)]:py-1
                      [@media(max-width:1000px)]:leading-3
                    "
                  >
                    <div
                      className="
                        break-words
                      "
                      title={
                        row.employeeName
                      }
                    >
                      {
                        row.employeeName
                      }
                    </div>
                  </td>


                  {/* ========================================================
                      EMPLOYEE CODE
                  ========================================================= */}

                  <td
                    className="
                      whitespace-nowrap
                      py-2
                      pr-2

                      [@media(max-width:1100px)]:py-1.5

                      [@media(max-width:1000px)]:py-1
                    "
                  >
                    {
                      row.employeeCode
                    }
                  </td>


                  {/* ========================================================
                      TEAM LEADER
                  ========================================================= */}

                  <td
                    className="
                      max-w-[125px]
                      py-2
                      pr-2

                      leading-4

                      [@media(max-width:1100px)]:py-1.5
                      [@media(max-width:1100px)]:leading-3

                      [@media(max-width:1000px)]:py-1
                      [@media(max-width:1000px)]:leading-3
                    "
                  >
                    <div
                      className="
                        break-words
                      "
                      title={
                        row.teamLeadName
                      }
                    >
                      {
                        row.teamLeadName
                      }
                    </div>
                  </td>


                  {/* ========================================================
                      SHIFT
                  ========================================================= */}

                  <td
                    className="
                      max-w-[90px]
                      py-2
                      pr-2

                      leading-4

                      [@media(max-width:1100px)]:py-1.5
                      [@media(max-width:1100px)]:leading-3

                      [@media(max-width:1000px)]:py-1
                      [@media(max-width:1000px)]:leading-3
                    "
                  >
                    <div
                      className="
                        break-words
                      "
                      title={
                        row.shiftName
                      }
                    >
                      {
                        row.shiftName
                      }
                    </div>
                  </td>


                  {/* ========================================================
                      LOCATION
                  ========================================================= */}

                  <td
                    className="
                      max-w-[100px]
                      py-2
                      pr-2

                      leading-4

                      [@media(max-width:1100px)]:py-1.5
                      [@media(max-width:1100px)]:leading-3

                      [@media(max-width:1000px)]:py-1
                      [@media(max-width:1000px)]:leading-3
                    "
                  >
                    <div
                      className="
                        break-words
                      "
                      title={
                        row.city
                      }
                    >
                      {
                        row.city
                      }
                    </div>
                  </td>


                  {/* ========================================================
                      CURRENT PRODUCTIVITY
                  ========================================================= */}

                  <td
                    className="
                      py-2
                      pr-2

                      [@media(max-width:1100px)]:py-1.5

                      [@media(max-width:1000px)]:py-1
                    "
                  >

                    <div
                      className="
                        flex
                        min-w-0
                        items-center

                        gap-2

                        [@media(max-width:1100px)]:gap-1.5

                        [@media(max-width:1000px)]:gap-1
                      "
                    >

                      {/* BAR */}

                      <div
                        className="
                          h-2
                          w-[90px]
                          shrink-0

                          rounded-full

                          bg-slate-100

                          [@media(max-width:1100px)]:h-1.5
                          [@media(max-width:1100px)]:w-[65px]

                          [@media(max-width:1000px)]:h-1.5
                          [@media(max-width:1000px)]:w-[55px]
                        "
                      >

                        <div
                          className={`
                            h-full
                            rounded-full

                            ${getBarColor(
                              row.currentProductivity,
                            )}
                          `}
                          style={{
                            width:
                              `${Math.min(
                                Math.max(
                                  row.currentProductivity,
                                  0,
                                ),
                                100,
                              )}%`,
                          }}
                        />

                      </div>


                      {/* VALUE */}

                      <span
                        className="
                          whitespace-nowrap
                          font-semibold

                          [@media(max-width:1000px)]:text-[8px]
                        "
                      >
                        {
                          row.currentProductivity.toFixed(
                            2,
                          )
                        }%
                      </span>

                    </div>

                  </td>


                  {/* ========================================================
                      PREVIOUS PRODUCTIVITY
                  ========================================================= */}

                  <td
                    className="
                      whitespace-nowrap
                      py-2
                      pr-2

                      [@media(max-width:1100px)]:py-1.5

                      [@media(max-width:1000px)]:py-1
                    "
                  >
                    {
                      row.previousProductivity.toFixed(
                        2,
                      )
                    }%
                  </td>


                  {/* ========================================================
                      CHANGE
                  ========================================================= */}

                  <td
                    className={`
                      whitespace-nowrap
                      py-2
                      pr-2
                      font-medium

                      ${
                        row.changePercent >=
                        0
                          ? 'text-green-600'
                          : 'text-red-500'
                      }

                      [@media(max-width:1100px)]:py-1.5

                      [@media(max-width:1000px)]:py-1
                    `}
                  >
                    {row.changePercent >
                    0
                      ? '+'
                      : ''}

                    {
                      row.changePercent.toFixed(
                        2,
                      )
                    }%
                  </td>


                  {/* ========================================================
                      STATUS
                  ========================================================= */}

                  <td
                    className="
                      py-2

                      [@media(max-width:1100px)]:py-1.5

                      [@media(max-width:1000px)]:py-1
                    "
                  >

                    <span
                      className={`
                        inline-flex
                        min-w-[68px]
                        items-center
                        justify-center

                        whitespace-nowrap

                        rounded-full

                        px-2
                        py-1

                        text-[10px]
                        font-medium
                        leading-3

                        ${getStatusColor(
                          row.status,
                        )}

                        [@media(max-width:1100px)]:min-w-[58px]
                        [@media(max-width:1100px)]:px-1.5
                        [@media(max-width:1100px)]:py-[3px]
                        [@media(max-width:1100px)]:text-[8px]
                        [@media(max-width:1100px)]:leading-3

                        [@media(max-width:1000px)]:min-w-[52px]
                        [@media(max-width:1000px)]:px-1
                        [@media(max-width:1000px)]:py-[2px]
                        [@media(max-width:1000px)]:text-[7px]
                        [@media(max-width:1000px)]:leading-3
                      `}
                    >
                      {
                        row.status
                      }
                    </span>

                  </td>

                </tr>

              ),
            )}

          </tbody>


          {/* =================================================================
              FOOTER / SPAN AVERAGE
          ================================================================= */}

          <tfoot>

            <tr
              className="
                border-t-2
                border-slate-300

                bg-[#FAFAFA]

                font-semibold
              "
            >

              {/* ============================================================
                  LABEL
              ============================================================= */}

              <td
                colSpan={6}
                className="
                  py-3
                  pr-2

                  whitespace-nowrap

                  [@media(max-width:1100px)]:py-2

                  [@media(max-width:1000px)]:py-1.5
                "
              >
                Span Average
              </td>


              {/* ============================================================
                  CURRENT AVERAGE
              ============================================================= */}

              <td
                className="
                  py-3
                  pr-2

                  [@media(max-width:1100px)]:py-2

                  [@media(max-width:1000px)]:py-1.5
                "
              >

                <div
                  className="
                    flex
                    items-center

                    gap-2

                    [@media(max-width:1100px)]:gap-1.5

                    [@media(max-width:1000px)]:gap-1
                  "
                >

                  <div
                    className="
                      h-2
                      w-[90px]
                      shrink-0

                      rounded-full

                      bg-slate-100

                      [@media(max-width:1100px)]:h-1.5
                      [@media(max-width:1100px)]:w-[65px]

                      [@media(max-width:1000px)]:h-1.5
                      [@media(max-width:1000px)]:w-[55px]
                    "
                  >

                    <div
                      className={`
                        h-full
                        rounded-full

                        ${getBarColor(
                          spanAverage.current,
                        )}
                      `}
                      style={{
                        width:
                          `${Math.min(
                            Math.max(
                              spanAverage.current,
                              0,
                            ),
                            100,
                          )}%`,
                      }}
                    />

                  </div>

                  <span
                    className="
                      whitespace-nowrap

                      [@media(max-width:1000px)]:text-[8px]
                    "
                  >
                    {
                      spanAverage.current.toFixed(
                        2,
                      )
                    }%
                  </span>

                </div>

              </td>


              {/* ============================================================
                  PREVIOUS AVERAGE
              ============================================================= */}

              <td
                className="
                  whitespace-nowrap
                  py-3
                  pr-2

                  [@media(max-width:1100px)]:py-2

                  [@media(max-width:1000px)]:py-1.5
                "
              >
                {
                  spanAverage.previous.toFixed(
                    2,
                  )
                }%
              </td>


              {/* ============================================================
                  CHANGE AVERAGE
              ============================================================= */}

              <td
                className={`
                  whitespace-nowrap
                  py-3
                  pr-2
                  font-medium

                  ${
                    spanAverage.change >=
                    0
                      ? 'text-green-600'
                      : 'text-red-500'
                  }

                  [@media(max-width:1100px)]:py-2

                  [@media(max-width:1000px)]:py-1.5
                `}
              >
                {
                  spanAverage.change >=
                  0
                    ? '+'
                    : ''
                }

                {
                  spanAverage.change.toFixed(
                    2,
                  )
                }%
              </td>


              {/* ============================================================
                  STATUS
              ============================================================= */}

              <td
                className="
                  whitespace-nowrap
                  py-3

                  [@media(max-width:1100px)]:py-2

                  [@media(max-width:1000px)]:py-1.5
                "
              >
                -
              </td>

            </tr>

          </tfoot>

        </table>

      </div>

    </div>
  );
};

export default AssociateProductivityTable;